import type { DocumentIntelligence, BatchResult, BatchIntelligence } from "@structurify/shared-types";

export const DOC_INTELLIGENCE: DocumentIntelligence = {
  summary: {
    one_line:
      "Routine water-damage claim, internally consistent. One narrative–field discrepancy and one policy-deductible question.",
    document_type: "P&C insurance claim — first notice of loss",
    intent: "Claimant seeking reimbursement for kitchen water damage from a burst supply line.",
  },
  risk: {
    score: 34,
    band: "Low–Moderate",
    rationale:
      "Standard plumbing failure pattern; no anomalous timing or amount; claimant tenure long; one narrative inconsistency to verify.",
  },
  derived: [
    {
      id: "claim_age_days",
      label: "Days since loss",
      value: "4 days",
      derivation: "signature_date − date_of_loss",
      note: "Within 30-day reporting window.",
      flag: "ok",
    },
    {
      id: "net_payable",
      label: "Net payable (est.)",
      value: "$16,920.00",
      derivation: "estimated_amount − deductible",
      note: "Before coverage limits applied.",
      flag: "ok",
    },
    {
      id: "amount_per_sqft",
      label: "Loss intensity",
      value: "$58 / sq ft (est.)",
      derivation: "estimated_amount ÷ damaged area inferred from narrative",
      note: "In line with regional averages for water damage.",
      flag: "ok",
    },
  ],
  field_chips: {
    estimated_amount: [
      { kind: "policy", text: "Within $20k auto-approval band", tone: "neutral" },
      { kind: "derived", text: "Net of deductible: $16,920", tone: "neutral" },
    ],
    deductible: [{ kind: "policy", text: "Matches policy HO-554918-A schedule", tone: "positive" }],
    date_of_loss: [{ kind: "derived", text: "4 days to filing — within window", tone: "positive" }],
    loss_description: [
      {
        kind: "contradiction",
        text: 'Narrative says "approximately three hours"; cross-check w/ neighbor statement requested',
        tone: "caution",
      },
    ],
    adjuster_name: [{ kind: "low_confidence", text: "Handwritten — verify spelling against roster", tone: "caution" }],
    insured_address: [{ kind: "derived", text: "Matches policy address on file", tone: "positive" }],
  },
  findings: [
    {
      kind: "contradiction",
      title: "Narrative duration vs. damage extent",
      body: 'The narrative reports water flowing for "approximately three hours" but the listed damage spans the kitchen floor, lower cabinetry, and the hallway runner. This appears consistent with three hours of unattended supply-line failure, but worth verifying against the plumber\'s report.',
      affects: ["loss_description", "estimated_amount"],
    },
    {
      kind: "policy",
      title: "Deductible interpretation",
      body: "Policy HO-554918-A shows two deductible tiers ($1,500 standard, $2,500 wind/hail). $1,500 is correct for water damage. No action needed.",
      affects: ["deductible"],
    },
    {
      kind: "missing",
      title: "No supporting documentation referenced",
      body: "Form does not reference attached photographs, plumber's invoice, or police/fire report. Standard for water claims at this amount but would strengthen the file.",
      affects: [],
    },
  ],
  actions: [
    { label: "Route to adjuster M. Castellano", primary: true, reason: "Matches assigned adjuster on form" },
    { label: "Request plumber invoice", primary: false, reason: "Strengthens narrative timing" },
    { label: "Approve under $20k auto-band", primary: false, reason: "Within policy fast-track threshold" },
  ],
};

function formatUSD(n: number): string {
  return "$" + Math.round(n).toLocaleString();
}

export function buildBatchIntelligence(batch: BatchResult): BatchIntelligence | null {
  if (!batch.rows.length) return null;
  const { rows } = batch;
  const total = rows.length;

  const amounts = rows.map((r) => parseFloat(r.amount.replace(/[$,]/g, "")) || 0);
  const totalAmount = amounts.reduce((a, b) => a + b, 0);
  const avgAmount = totalAmount / Math.max(1, total);
  const maxAmount = Math.max(...amounts);
  const maxIdx = amounts.indexOf(maxAmount);
  const maxRow = rows[maxIdx];

  const typeCount: Record<string, number> = {};
  for (const r of rows) {
    typeCount[r.type] = (typeCount[r.type] ?? 0) + 1;
  }
  const topTypeEntry = Object.entries(typeCount).sort((a, b) => b[1] - a[1])[0];

  const anomalies: BatchIntelligence["anomalies"] = [];
  for (let i = 0; i < amounts.length; i++) {
    const a = amounts[i] ?? 0;
    const row = rows[i];
    if (a > avgAmount * 2.2 && row) {
      anomalies.push({
        kind: "amount",
        title: `${row.insured} — ${formatUSD(a)} is ${(a / avgAmount).toFixed(1)}× batch average`,
        row: i,
      });
    }
  }
  // Cluster: same type appears ≥ 3 times
  for (const [type, count] of Object.entries(typeCount)) {
    if (count >= 3) {
      anomalies.push({
        kind: "pattern",
        title: `${count} "${type}" claims in this batch`,
        body: "High concentration of a single loss type. Consider cross-referencing for a shared underlying cause.",
      });
    }
  }

  // Possible duplicates: pairs of files sharing the first 8 chars of their base name
  const names = rows.map((r) => r.file.replace(/\.[^.]+$/, ""));
  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const a = names[i] ?? "";
      const b = names[j] ?? "";
      if (a.length >= 8 && b.length >= 8 && a.slice(0, 8) === b.slice(0, 8)) {
        anomalies.push({
          kind: "duplicate",
          title: `Possible duplicate: ${rows[i]?.file} and ${rows[j]?.file}`,
          body: "Files share a common name prefix. Manual confirmation suggested.",
          row: i,
        });
      }
    }
  }

  const flagged = rows.filter((r) => r.flagged || r.confidence < 0.85).length;
  const ready = total - flagged;
  const avgConfidence = rows.reduce((s, r) => s + r.confidence, 0) / Math.max(1, total);

  const findings: BatchIntelligence["findings"] = [
    {
      kind: "trend",
      title: topTypeEntry ? `${topTypeEntry[1]} of ${total} claims are "${topTypeEntry[0]}"` : "Loss-type breakdown available",
      body: "Most common loss type in this batch. Review distribution chart for full mix.",
    },
    {
      kind: "policy",
      title: `${rows.filter((r) => parseFloat(r.amount.replace(/[$,]/g, "")) > 20000).length} claims exceed $20k auto-approval band`,
      body: "These will require senior adjuster sign-off per policy fast-track threshold.",
    },
    {
      kind: "quality",
      title: `${flagged} document${flagged !== 1 ? "s" : ""} flagged for human review`,
      body: "Reasons include uncertain extracted values (confidence < 85%) and policy-rule mismatches.",
    },
  ];

  return {
    metrics: {
      total_documents: total,
      total_amount: formatUSD(totalAmount),
      avg_amount: formatUSD(avgAmount),
      max_amount: formatUSD(maxAmount),
      max_row: maxRow ?? rows[0] ?? { file: "", insured: "", type: "", amount: "$0", date: "", confidence: 0, flagged: false, failed: false },
      ready_count: ready,
      flagged_count: flagged,
      avg_confidence: avgConfidence,
      top_loss_type: topTypeEntry ? `${topTypeEntry[0]} (${topTypeEntry[1]})` : "—",
    },
    type_distribution: Object.entries(typeCount).sort((a, b) => b[1] - a[1]) as Array<[string, number]>,
    anomalies,
    findings,
    summary: `${total} claims totaling ${formatUSD(totalAmount)}. ${ready} ready to export, ${flagged} need a human eye. ${anomalies.length} potential issues to investigate.`,
  };
}
